import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

interface UploadParams {
  files: File[];
  startIdx: number;
}

export const useUploadDocuments = ({
  handleUploadSuccess,
  handleUploadError,
}: {
  handleUploadSuccess?: (data: { documentUrls: string[]; startIdx: number; files: File[] }) => void;
  handleUploadError?: (
    error: Error,
    meta: { startIdx: number; count: number; fileNames: string[] },
  ) => void;
}) => {
  const mutation = useMutation({
    mutationFn: ({ files, startIdx }: UploadParams) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "PortServiceDocumentUpload",
          action: "upload_port_service_document",
          entityType: "port_service_document",
        },
        async (transactionId) => {
          const documentUrls: string[] = [];
          for (const file of files) {
            const payloadForSasToken = {
              prefix: SasTokenPrefix.PORT_SERVICES,
              contentType: file.type || null,
              privilege: SasTokenPrivilege.WRITE,
              blobUrl: null,
            };
            // const { blobUrl, sasToken } = await getSasToken(payloadForSasToken);
            let sasTokenResponse: Awaited<ReturnType<typeof getSasToken>>;
            try {
              sasTokenResponse = await getSasToken(
                payloadForSasToken,
                ...(transactionId ? [transactionId] : []),
              );
            } catch (error) {
              const normalizedError =
                error instanceof Error ? error : new Error("Failed to obtain SAS token from API.");
              handleUploadError?.(normalizedError, {
                startIdx,
                count: files.length,
                fileNames: files.map((f) => f.name),
              });
              return;
            }
            const { blobUrl, sasToken } = sasTokenResponse;
            const result = await uploadFileToAzureBlob({
              blobUrl,
              sasToken,
              file,
              certificateType: "PORT_SERVICES",
            });
            result.forEach((r) => documentUrls.push(r.documentUrl));
          }
          return { documentUrls, startIdx, files };
        },
      ),
    onSuccess: (data) => {
      if (!data) return;
      handleUploadSuccess?.(data);
    },
    onError: (error: Error, variables: UploadParams) => {
      handleUploadError?.(error, {
        startIdx: variables.startIdx,
        count: variables.files.length,
        fileNames: variables.files.map((f) => f.name),
      });
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending,
  };
};
